import { useState, useEffect } from 'react'
import { getParametres, login, register, getMe, logout, verifyEmail, resendVerification, confirmRegistration, resendRegistrationCode } from '../services/api'
import { invalidateCache } from '../utils/cache'
import { resetDataRevision } from './useFreshData'

// Invalider le cache des données utilisateur pour forcer un rechargement frais après login
function invalidateUserDataCache() {
  try {
    resetDataRevision()
    ;['/parametres', '/stats', '/operations', '/categories', '/comptes', '/graphiques'].forEach(invalidateCache)
  } catch (e) {
    console.warn('Invalidation cache:', e)
  }
}

function readStoredToken() {
  try {
    return localStorage.getItem('token')
  } catch (e) {
    // Mode privé ou stockage désactivé : pas de token persistant
    return null
  }
}

export function useAuth() {
  const [user, setUser] = useState(null)
  const storedToken = readStoredToken()
  const [token, setToken] = useState(storedToken)
  const [onboardingCompleted, setOnboardingCompleted] = useState(false)
  // Si pas de token, on ne bloque pas le rendu
  const [loading, setLoading] = useState(!!storedToken)

  useEffect(() => {
    console.log('🔐 useAuth: Initialisation')
    // Vérifier si un token existe au chargement
    const storedToken = readStoredToken()
    console.log('🔐 useAuth: Token stocké:', storedToken ? 'présent' : 'absent')
    if (storedToken) {
      setToken(storedToken)
      checkAuth()
    } else {
      console.log('🔐 useAuth: Pas de token, chargement terminé')
      setLoading(false)
    }

    // Écouter les changements de token dans localStorage (autres onglets).
    // Comparer e.newValue à localStorage était sans effet : localStorage contient
    // déjà la nouvelle valeur quand l'événement arrive, donc la connexion dans un
    // autre onglet n'était jamais répercutée ici.
    const handleStorageChange = (e) => {
      if (e.key !== 'token') return
      const newToken = e.newValue
      if (newToken) {
        setToken((previous) => {
          if (previous !== newToken) checkAuth()
          return newToken
        })
      } else {
        setToken(null)
        setUser(null)
        setOnboardingCompleted(false)
      }
    }

    // Écouter les événements de stockage (pour les changements depuis d'autres onglets)
    window.addEventListener('storage', handleStorageChange)

    // Écouter les événements personnalisés pour les changements dans le même onglet
    const handleTokenChange = () => {
      const newToken = readStoredToken()
      if (!newToken) {
        // Stockage vide ou indisponible (mode privé, stockage désactivé) : ne
        // pas détruire une session déjà établie en mémoire. handleLogin émet
        // lui-même cet événement ; quand l'écriture du jeton a échoué, la
        // relecture rend null et la connexion s'annulait aussitôt.
        // Une déconnexion venue d'un autre onglet passe par l'événement
        // `storage`, qui porte l'information de suppression.
        return
      }
      setToken((previous) => {
        // Jeton inchangé : la connexion qui vient d'émettre l'événement a déjà
        // chargé l'utilisateur et les paramètres. Rappeler checkAuth() ici
        // doublait les requêtes et annulait la session si l'une d'elles
        // échouait — au mépris du repli prévu dans handleLogin.
        if (previous !== newToken) checkAuth()
        return newToken
      })
    }
    window.addEventListener('tokenChanged', handleTokenChange)
    
    // Écouter les événements de completion d'onboarding
    const handleOnboardingCompleted = async () => {
      try {
        const params = await getParametres(true)
        setOnboardingCompleted(params.onboarding_completed === 1)
      } catch (error) {
        console.error('Erreur lors de la vérification de l\'onboarding:', error)
      }
    }
    window.addEventListener('onboardingCompleted', handleOnboardingCompleted)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('tokenChanged', handleTokenChange)
      window.removeEventListener('onboardingCompleted', handleOnboardingCompleted)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const checkAuth = async () => {
    console.log('🔐 useAuth: Vérification de l\'authentification...')
    try {
      const userData = await getMe()
      console.log('🔐 useAuth: Utilisateur récupéré:', userData)
      setUser(userData.user)
      
      // Vérifier l'onboarding (forceRefresh pour éviter cache obsolète après reconnexion)
      try {
        const params = await getParametres(true)
        setOnboardingCompleted(params.onboarding_completed === 1)
      } catch (error) {
        console.error('Erreur lors de la vérification de l\'onboarding:', error)
        setOnboardingCompleted(false)
      }
    } catch (error) {
      // Token invalide ou erreur réseau, déconnecter
      console.error('Erreur lors de la vérification de l\'authentification:', error)
      localStorage.removeItem('token')
      setToken(null)
      setUser(null)
      setOnboardingCompleted(false)
    } finally {
      setLoading(false)
    }
  }

  const handleLogin = async (username, password) => {
    try {
      // Invalider le cache avant la connexion
      invalidateCache('/auth/me')
      
      const response = await login({ username, password })
      if (response.success && response.token) {
        const token = response.token
        try {
          localStorage.setItem('token', token)
        } catch (e) {
          // Sur mobile (mode privé, stockage désactivé), localStorage peut échouer
        }
        setToken(token)
        
        // Header explicite pour les requêtes juste après login (évite 401 sur mobile si localStorage indisponible)
        const authHeaders = { headers: { Authorization: `Bearer ${token}` } }
        
        // Utiliser les données de la réponse, mais aussi récupérer depuis le serveur pour être sûr
        if (response.user) {
          setUser(response.user)
        }
        
        // Récupérer les données fraîches avec le token explicite (évite 401 sur mobile)
        try {
          const freshUserData = await getMe(true, authHeaders)
          if (freshUserData && freshUserData.user) {
            console.log('🔐 useAuth: Utilisateur frais récupéré après connexion:', freshUserData.user)
            setUser(freshUserData.user)
          }
        } catch (error) {
          console.warn('⚠️ Impossible de récupérer les données utilisateur fraîches, utilisation des données de la réponse:', error)
          if (response.user) {
            setUser(response.user)
          }
        }
        
        window.dispatchEvent(new Event('tokenChanged'))
        invalidateUserDataCache()
        // Vérifier l'onboarding avec le token explicite (forceRefresh pour persistance après reconnexion)
        try {
          const params = await getParametres(true, authHeaders)
          const onboardingStatus = params.onboarding_completed === 1
          setOnboardingCompleted(onboardingStatus)
          window.dispatchEvent(new Event('paramsChanged'))
          return { success: true, onboardingCompleted: onboardingStatus }
        } catch (error) {
          setOnboardingCompleted(false)
          return { success: true, onboardingCompleted: false }
        }
      }
      return { success: false, message: response.message || 'Erreur de connexion' }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Erreur de connexion'
      }
    }
  }

  const handleRegister = async (username, email, password) => {
    try {
      // Le cache est déjà nettoyé dans la fonction register() de api.js
      const response = await register({ username, email, password })
      
      // Inscription : le compte n'est créé qu'après vérification du code (envoyé par email uniquement)
      if (response.success) {
        return {
          success: true,
          requiresVerification: true,
          email: response.email
        }
      }
      
      // Si pas de succès, extraire le message d'erreur
      let errorMessage = response.message || 'Erreur d\'enregistrement'
      
      // Si des erreurs de validation sont présentes, les formater
      if (response.errors) {
        const errorList = Object.entries(response.errors)
          .map(([field, messages]) => {
            const fieldName = field === 'password' ? 'mot de passe' : field
            const msg = Array.isArray(messages) ? messages.join(', ') : messages
            return `${fieldName}: ${msg}`
          })
          .join('\n')
        errorMessage = errorList || errorMessage
      }
      
      return { success: false, message: errorMessage }
    } catch (error) {
      console.error('Erreur lors de l\'inscription:', error)
      // Toujours renvoyer un message lisible pour l'écran de création de compte
      let errorMessage = 'Erreur d\'enregistrement'
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.response?.data?.errors) {
        const errorList = Object.entries(error.response.data.errors)
          .map(([field, messages]) => {
            const fieldName = field === 'password' ? 'mot de passe' : field
            const msg = Array.isArray(messages) ? messages.join(', ') : messages
            return `${fieldName}: ${msg}`
          })
          .join('\n')
        errorMessage = errorList || errorMessage
      } else if (error.code === 'ECONNRESET' || error.message?.includes('ECONNRESET') || (error.response?.status === 500 && !error.response?.data?.message)) {
        errorMessage = 'Le serveur a coupé la connexion. Vérifiez que le backend est lancé (local) ou que l’URL backend (VITE_API_URL) répond (distant).'
      } else if (error.response?.status === 502) {
        errorMessage = 'Backend injoignable. Vérifiez que le backend est lancé ou que l’URL (VITE_API_URL) est correcte.'
      } else if (error.message) {
        errorMessage = error.message
      }
      return { success: false, message: errorMessage }
    }
  }

  const handleVerifyEmail = async (code) => {
    try {
      const response = await verifyEmail(code)
      if (response.success) {
        setUser(response.user)
        // Recharger le profil complet depuis le serveur (email_verified + autres champs)
        try {
          const freshUser = await getMe(true)
          if (freshUser?.user) setUser(freshUser.user)
        } catch (e) {
          console.warn('Rechargement profil après vérification:', e)
        }
        window.dispatchEvent(new Event('emailVerified'))
        return { success: true, message: response.message }
      }
      return { success: false, message: response.message || 'Code invalide' }
    } catch (error) {
      console.error('Erreur lors de la vérification de l\'email:', error)
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Erreur lors de la vérification'
      }
    }
  }

  const handleResendVerification = async () => {
    try {
      const response = await resendVerification()
      if (response.success) {
        return { success: true, message: response.message }
      }
      return { success: false, message: response.message || 'Erreur lors du renvoi' }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Erreur lors du renvoi'
      }
    }
  }

  /** Confirme l'inscription avec le code reçu par email : crée le compte et connecte l'utilisateur. */
  const handleConfirmRegistration = async (email, code) => {
    try {
      const response = await confirmRegistration(email, code)
      if (response.success && response.token && response.user) {
        try {
          localStorage.setItem('token', response.token)
        } catch (e) {
          // Sur mobile (mode privé), localStorage peut échouer : le token reste en mémoire
        }
        setToken(response.token)
        setUser(response.user)
        invalidateCache('/auth/me')
        setOnboardingCompleted(false)
        window.dispatchEvent(new Event('tokenChanged'))
        return { success: true, message: response.message }
      }
      return { success: false, message: response.message || 'Code invalide ou expiré' }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Erreur lors de la création du compte'
      }
    }
  }

  /** Renvoie le code d'inscription (avant création du compte). */
  const handleResendRegistrationCode = async (email) => {
    try {
      const response = await resendRegistrationCode(email)
      if (response.success) {
        return { success: true, message: response.message }
      }
      return { success: false, message: response.message || 'Erreur lors du renvoi' }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Erreur lors du renvoi'
      }
    }
  }

  const handleLogout = async () => {
    try {
      // Appeler l'API de déconnexion (optionnel, peut échouer sans problème)
      try {
        await logout()
      } catch (error) {
        // Ignorer les erreurs de l'API, on nettoie quand même côté client
        console.warn('Erreur lors de l\'appel API de déconnexion:', error)
      }
    } finally {
      // Toujours nettoyer l'état local, même si l'API échoue
      invalidateUserDataCache()
      const { clearAllCache } = await import('../utils/cache')
      clearAllCache()
      resetDataRevision()
      localStorage.removeItem('token')
      setToken(null)
      setUser(null)
      setOnboardingCompleted(false)
      
      // Déclencher un événement pour notifier les autres composants
      window.dispatchEvent(new Event('userLoggedOut'))
    }
  }

  return {
    user,
    token,
    isAuthenticated: !!token && !!user,
    onboardingCompleted,
    loading,
    login: handleLogin,
    register: handleRegister,
    logout: handleLogout,
    verifyEmail: handleVerifyEmail,
    resendVerification: handleResendVerification,
    confirmRegistration: handleConfirmRegistration,
    resendRegistrationCode: handleResendRegistrationCode,
    setOnboardingCompleted
  }
}

